import { useMemo, useState } from "react";
import { ChevronDown, Volume2, Star, Trash2, History as HistoryIcon, Search, Copy, Share2 } from "lucide-react";

const ago = (iso) => {
  const mins = Math.max(Math.round((Date.now() - new Date(iso).getTime()) / 60000), 0);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24);
  return d < 7 ? `${d}d ago` : new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const ACTION_LABEL = { played: "heard", practised: "practised", reviewed: "reviewed" };

// Phrase Book history: what you practised recently, and everything you saved, grouped by pack.
export const PhraseHistory = ({ history, playing, onSpeak, onSave, onDelete, onCopy, onShare, onForget }) => {
  const [tab, setTab] = useState("recent");
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState({});

  const titles = history?.pack_titles || {};
  const order = [...(history?.pack_order || []), "translations", "suggested", "other"];

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = (tab === "saved" ? history?.saved : history?.recent) || [];
    const matched = rows.filter(
      (r) => !q || `${r.text} ${r.translation || ""}`.toLowerCase().includes(q)
    );
    const buckets = {};
    matched.forEach((r) => {
      const id = titles[r.pack_id] ? r.pack_id : "other";
      (buckets[id] = buckets[id] || []).push(r);
    });
    const rank = (id) => (order.indexOf(id) === -1 ? 999 : order.indexOf(id));
    return Object.keys(buckets)
      .sort((a, b) => rank(a) - rank(b))
      .map((id) => ({ id, label: titles[id] || "Other", rows: buckets[id] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, tab, query]);

  const total = groups.reduce((n, g) => n + g.rows.length, 0);
  const savedCount = history?.saved?.length || 0;

  const Row = ({ r }) => {
    const key = r.phrase_id || r.key || r.text;
    const id = (r.text || "").slice(0, 14);
    return (
      <li
        data-testid={`history-phrase-${id}`}
        className="flex items-start justify-between gap-3 rounded-2xl bg-page px-4 py-3 text-sm"
      >
        <span className="min-w-0">
          <span dir="auto" className="block font-medium">{r.text}</span>
          <span dir="auto" className="block text-xs text-ink-soft">
            {r.translation}
            {r.phonetic ? ` · ${r.phonetic}` : ""}
            {r.last_at ? ` · ${ACTION_LABEL[r.action] || "practised"} ${ago(r.last_at)}` : ""}
            {!r.last_at && r.created_at ? ` · saved ${ago(r.created_at)}` : ""}
          </span>
        </span>
        <span className="flex shrink-0 items-center gap-1.5">
          <button
            data-testid={`history-speak-${id}`}
            onClick={() => onSpeak(r, false)}
            aria-label={`Hear ${r.text}`}
            title="Hear it out loud"
            className={`rounded-full border border-white/10 p-1.5 transition-colors hover:border-brand/50 hover:text-brand ${
              playing === r.text ? "border-brand/60 text-brand" : "text-ink-soft"
            }`}
          >
            <Volume2 className="h-3.5 w-3.5" />
          </button>
          <button
            data-testid={`history-slow-${id}`}
            onClick={() => onSpeak(r, true)}
            aria-label={`Hear ${r.text} slowly`}
            title="Hear it slowly, syllable by syllable"
            className={`rounded-full border border-white/10 px-2 py-1 text-[10px] font-bold tracking-wide transition-colors hover:border-brand/50 hover:text-brand ${
              playing === `slow:${r.text}` ? "border-brand/60 text-brand" : "text-ink-soft"
            }`}
          >
            {playing === `slow:${r.text}` ? "…" : "SLOW"}
          </button>
          <button
            data-testid={`history-copy-${id}`}
            onClick={() => onCopy(r)}
            aria-label="Copy this phrase"
            title="Copy the phrase and its meaning"
          >
            <Copy className="h-3.5 w-3.5 text-ink-soft hover:text-brand" />
          </button>
          <button
            data-testid={`history-share-${id}`}
            onClick={() => onShare(r)}
            aria-label="Share this phrase"
            title="Send a link to a friend"
          >
            <Share2 className="h-3.5 w-3.5 text-ink-soft hover:text-brand" />
          </button>
          {tab === "recent" ? (
            <>
              <button
                data-testid={`history-save-${id}`}
                onClick={() => !r.saved && onSave(r)}
                aria-label={r.saved ? "Already saved" : "Save this phrase"}
                title={r.saved ? "Already in your saved phrases" : "Save this phrase"}
              >
                <Star className={`h-4 w-4 ${r.saved ? "fill-brand text-brand" : "text-ink-soft hover:text-brand"}`} />
              </button>
              <button
                data-testid={`history-forget-${id}`}
                onClick={() => onForget(r)}
                aria-label="Remove from my history"
                title="Remove from my history"
              >
                <Trash2 className="h-4 w-4 text-ink-soft hover:text-destructive" />
              </button>
            </>
          ) : (
            <button
              data-testid={`history-delete-${key}`}
              onClick={() => onDelete(r)}
              aria-label="Remove from saved"
            >
              <Trash2 className="h-4 w-4 text-ink-soft hover:text-destructive" />
            </button>
          )}
        </span>
      </li>
    );
  };

  return (
    <div className="rounded-3xl border border-white/10 bg-panel p-7" data-testid="phrase-history">
      <p className="inline-flex items-center gap-2 font-display text-lg font-bold tracking-tight">
        <HistoryIcon className="h-4 w-4 text-brand" /> History
      </p>
      <p className="mt-1 text-sm text-ink-soft">
        Everything you have been practising, and everything you kept — tap the speaker or SLOW on any line to
        read along.
      </p>

      <div className="mt-4 flex gap-1 rounded-full bg-surfacealt p-1">
        {[
          ["recent", `Recent${history?.recent?.length ? ` (${history.recent.length})` : ""}`],
          ["saved", `Saved${savedCount ? ` (${savedCount})` : ""}`],
        ].map(([id, label]) => (
          <button
            key={id}
            data-testid={`phrase-history-tab-${id}`}
            onClick={() => setTab(id)}
            className={`flex-1 rounded-full px-3 py-2 text-xs font-semibold uppercase tracking-wide transition-colors ${
              tab === id ? "bg-brand text-white" : "text-ink-soft hover:text-ink"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="relative mt-3">
        <Search className="pointer-events-none absolute start-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-soft" />
        <input
          data-testid="phrase-history-search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search your phrases…"
          className="min-h-[40px] w-full rounded-full border border-white/10 bg-page ps-9 pe-3 text-sm outline-none placeholder:text-ink-soft focus:border-brand/50"
        />
      </div>

      <div className="mt-4 space-y-4">
        {groups.map((g) => (
          <div key={g.id} data-testid={`phrase-history-group-${g.id}`}>
            <button
              data-testid={`phrase-history-toggle-${g.id}`}
              onClick={() => setCollapsed((c) => ({ ...c, [g.id]: !c[g.id] }))}
              className="flex w-full items-center gap-1.5 py-1 text-start text-[11px] font-semibold uppercase tracking-widest text-ink-soft transition-colors hover:text-ink"
            >
              <ChevronDown className={`h-3 w-3 transition-transform ${collapsed[g.id] ? "-rotate-90" : ""}`} />
              {g.label}
              <span className="ms-auto rounded-full bg-surfacealt px-2 py-0.5 text-[10px]">{g.rows.length}</span>
            </button>
            {!collapsed[g.id] && (
              <ul className="mt-2 space-y-2">
                {g.rows.map((r) => (
                  <Row key={r.phrase_id || r.key || r.text} r={r} />
                ))}
              </ul>
            )}
          </div>
        ))}
        {total === 0 && (
          <p className="text-sm text-ink-soft" data-testid="phrase-history-empty">
            {tab === "saved"
              ? "Nothing saved yet — tap the star on a recent phrase, or the bookmark on any message in Messages."
              : query
              ? "No phrases match that."
              : "Nothing yet — play a phrase or take a pack quiz and it will show up here."}
          </p>
        )}
      </div>
    </div>
  );
};
